import _ from "underscore";

import {Questionary} from "../questionary";
import Cell from "./cell";
import {Form} from "./form";
import {ItemGroup} from "../groups/itemGroup";
import {GroupRepository} from "../groups/groupRepository";
import {XmlTagInfo} from "../xmlTagProvider";
import {CellItem} from "../items/cellItem";
import {LabelItem} from "../items/labelItem";
import {Event, CellAction, SubmitForm} from "../tags/expression";
import {XmlUtils} from "../utils";

export default class Grid {

    randomize: {
        rows? : number[][];
        columns? : number[][];
    } = {};


    // Index of by cellId. It can contain duplicates (on cellSpan each original cellId and the first cellId refers to the same Cell)
    // Never use to iterate all Cells - use .cells property instead.
    cellById!: { [id:string]: Cell };

    layout!: Cell[][]; //{ [row:number]: { [column: number]: Cell } };

    spans!: any[];
    cells!: Cell[];


    private constructor(
        public questionary: Questionary,
        public readonly form: Form,
        public rows: number,
        public columns: number,
        private groups: _.Dictionary<ItemGroup<CellItem>>
    ) { }

    static fromXML(node: XmlTagInfo, questionary: Questionary, form: Form) {

        const childrenByTag = _.groupBy(node.children, 'tag');

        // Create groups
        const groupTags = childrenByTag['groups'] ? childrenByTag['groups'][0].children : [];
        const groups: ItemGroup<CellItem>[] = groupTags.map( (node:any) => {
            const G = GroupRepository.byName(node.tag);
            return new G(node, form);
        });

        const grid = new Grid(
            questionary,
            form,
            parseInt(node.attributes['rows']),
            parseInt(node.attributes['columns']),
            _.indexBy(groups, 'id')
        );


        // Load cells
        grid.cells = XmlUtils.childrenByName(node, 'cell').map(n => Cell.fromXml(n, grid));
        grid.cellById = _.indexBy(grid.cells, 'id');

        grid.spans = childrenByTag['cellSpanList'] ? childrenByTag['cellSpanList'][0].children : null;

        const randomize = _.indexBy<XmlTagInfo>(childrenByTag['randomize'][0].children, 'tag');

        if(randomize['rows'] && randomize['rows'].value){
            grid.randomize.rows = JSON.parse(randomize['rows'].value);
        }

        if(randomize['columns'] && randomize['columns'].value){
            grid.randomize.columns = JSON.parse(randomize['columns'].value);
        }

        // Init groups cells
        grid.cells.forEach(cell => cell.updateGroups());

        return grid;
    }

    groupById(id: string) {
        return this.groups[id];
    }

    applyRandomize (permutation?: any) {

        const randomizeSelected = (count: number, config: any) => {
            const result = _.range(count);
            if (!config) {
                return result;
            }
            _.each(config, (places: number[]) => {
                if (places.length > 0) {
                    const shuffled = _.shuffle<number>(places);
                    for (let i = 0; i < places.length; i++) {
                        result[places[i]] = shuffled[i];
                    }
                }
            });
            return result;
        };

        const isPermutationValid = (perm?: any): boolean => {
            if( !_.isObject(perm) || _.isUndefined(perm.rows) || _.isUndefined(perm.columns) ) {
                return false;
            }
            return perm.rows.length === this.rows && perm.columns.length === this.columns;
        };

        function isRandomized(config: any) {
            return config.rows?.length || config.columns?.length;
        }

        if( !isRandomized(this.randomize) ) {
            return false;
        }
        // TODO: there might be incorrect data saved, report error!
        if (!isPermutationValid(permutation)) {
            permutation = {
                rows: randomizeSelected(this.rows, this.randomize.rows),
                columns: randomizeSelected(this.columns, this.randomize.columns)
            };
        }

        // rearrange cells
        this.cells.forEach((c:Cell) => {
            c.row = permutation.rows[c.row];
            c.column = permutation.columns[c.column];
        });

        return permutation;
    }

    createLayout () {
        this.layout = _(this.rows).range().map(() => new Array<Cell>(this.columns));

        this.cells.forEach((cell: Cell) => {
            if (!this.layout[cell.row]) {
                throw new Error(`Cell ${cell.id} in form ${cell.grid.form.id} cannot be written. Row ${cell.row} not present in layout.`);
            }
            this.layout[cell.row][cell.column] = cell;
        });

        // fill empty space
        _.each(this.layout, (row, y) => {
            _.each(row, (cell, x) => {
                if(!cell){
                    cell = Cell.emptyCell(this, x, y);
                    this.cellById[cell.id] = cell;
                }
                this.layout[y][x] = cell;
            });
        });

        // apply spans
        _(this.spans).each((s) => {
            let firstCell = this.layout[s.y][s.x];
            firstCell.span = {
                width: s.width,
                height: s.height
            };

            for (let x = s.x; x < s.x + s.width; x++) {
                for (let y = s.y; y < s.y + s.height; y++) {
                    let cell = this.layout[y][x];
                    if (firstCell !== cell) {
                        firstCell.cellItems = firstCell.cellItems.concat(cell.cellItems);
                        firstCell.width = Math.max(firstCell.width, cell.width);
                        firstCell.height = Math.max(firstCell.height, cell.height);

                        // @ts-ignore
                        this.layout[y][x] = null; // insert null (nothing) in case of cellspan
                        this.cellById[cell.id] = firstCell;
                    }
                }
            }
        });
    }

    eachControl (callback: (control: CellItem) => void) {
        this.cells.forEach(c => c.cellItems.forEach(callback));
    }

    public get cellItems(): CellItem[] {
        const items: CellItem[] = [];
        this.eachControl(c => items.push(c));
        return items;
    }

    processEvents(events: Event[]) {
        const visibleMap : { [id:string]: boolean } = {};
        const enabledMap : { [id:string]: boolean } = {};
        let submit = false;

        const executeAction = (ids: string[], map:any, value: boolean, init: boolean, eventIsActive: boolean) => {
            ids.forEach( id => {
                if (init) {
                    // Init to negative value
                    map[id] = !value;
                } else {
                    if (eventIsActive) {
                        // If event is active replace by value (otherwise left it untouched)
                        map[id] = value;
                    }
                }
            });
        };

        const processActions = (init: boolean) => {
            events.forEach((event: Event) => {
                const a = event.action;
                const eventIsActive = event.condition.evaluate(this.questionary.state);
                if( eventIsActive && !init ) {
                    this.questionary.trigger(Questionary.Events.NOTIFY_EVENT_ACTIVE, event.describe())
                }
                if( a instanceof CellAction ) {
                    switch (a.action) {
                        case 'show':
                            executeAction(a.items, visibleMap, true, init, !!eventIsActive);
                            break;
                        case 'hide':
                            executeAction(a.items, visibleMap, false, init, !!eventIsActive);
                            break;
                        case 'enable':
                            executeAction(a.items, enabledMap, true, init, !!eventIsActive);
                            break;
                        case 'disable':
                            executeAction(a.items, enabledMap, false, init, !!eventIsActive);
                            break;
                        default:
                        // TODO log unsupported action
                    }
                } else if( a instanceof SubmitForm && eventIsActive) {
                    submit = true;
                }
            });
        };

        processActions(true);
        processActions(false);

        _.each(visibleMap, (value, id) => this.cellById[id].visible = value);
        _.each(enabledMap, (value, id) => this.cellById[id].enabled = value);

        return submit;
    }

    init(): void {
        _.each(this.groups, g => g.init());
        this.cells.forEach(cell => cell.initControls());
    }

    updateActiveCells(){
        this.cells.forEach((cell)=> {
            cell.active = _(cell.cellItems).reduce((m:boolean, c:CellItem) => m || c.active(), false);
        });
    }

    isRowVisible(row: number): boolean {
        const cells = this.layout[row];
        return cells.some(c => c && c.isVisible());
    }
    isColumnVisible(column: number): boolean {
        const cells = this.layout.map(row => row[column]);
        return cells.some(c => c && c.isVisible());
    }
    isLabelRow(row: number): boolean {
        return row === 0 && this.layout[row]
                    .map(cell => cell ? cell.cellItems : [])
                    .reduce((acc, v) => acc.concat(v), [])  // flatten
                    .every(c => c instanceof LabelItem);
    }
    columnWidth(column: number): number {
        const cells = this.layout.map(row => row[column]);
        return cells.filter(c => c).map(c => c.width).reduce((acc, value) => Math.max(acc, value), 0);
    }
    rowHeight(row: number): number {
        const cells = this.layout[row];
        return cells.filter(c => c).map(c => c.height).reduce((acc, value) => Math.max(acc, value), 0);
    }
}
